import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout, { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "../lib/categories";
import { Boxes, ClipboardCheck, Search, BarChart3, Plus, X, Save, Trash2, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { blisterLabel, isIndividual, unitText } from "../lib/stock";
import { PremiumToggle } from "../components/FlavorQuickAdd";

// Kitchen employees may only add flavors in these categories (matches server enum)
const KITCHEN_ADDABLE = ["macaron", "macaron_seasonal", "gelato_pan", "gelato_pint", "gelato_pop"];
// Blister-tray-counted categories (Feb 2026 client rule): staff count how
// many FULL blister trays (70 macarons each) and one PARTIAL box (0–70
// remaining macarons) they have. Only applies to macaron & seasonal
// macaron. No "loose" or fixed-52 logic — a partial IS the loose count.
const BLISTER_CATEGORIES = new Set(["macaron", "macaron_seasonal"]);
const FULL_TRAY = 70;

/** Turn an entry (number OR {full,partial}) into the recount total. */
function totalOf(entry) {
  if (entry === "" || entry === undefined || entry === null) return null;
  if (typeof entry === "number") return entry;
  const f = Number(entry.full) || 0;
  const p = Number(entry.partial) || 0;
  // If both are empty and never touched, treat as blank (skip on submit)
  if (f === 0 && p === 0 && !entry.touched) return null;
  return f * FULL_TRAY + p;
}

/**
 * Kitchen / Inventory dashboard used by BOTH admin and kitchen employees.
 * Two tabs: Current Inventory + Today's Count (formerly "Daily Production").
 *
 * Feb 2026 launch changes:
 *  • Today's Count is an ABSOLUTE recount (walk the shop, type totals) — the
 *    submit endpoint SETS `current_stock` instead of adding to it.
 *  • Macaron rows show a blister widget: [Full] × 70 + [Partial] × 52 = total.
 *  • Admin gets full CRUD on the Current Inventory tab (Actual Count input,
 *    Edit price/threshold, Available toggle, Delete, Add Item) — parity with
 *    Catalog so admin doesn't have to bounce tabs.
 */
export default function EmployeeKitchen() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const isKitchen = user?.role === "employee" || !!user?.warehouse;
  // Both admin and kitchen staff get the full editable Current Inventory
  // table (set count, toggle Available, edit, delete, add). The Price
  // column + Edit-price flow stay admin-only — kitchen staff never see
  // or set prices.
  const canEditInventory = isAdmin || isKitchen;
  const canSeePrice = isAdmin;
  const [tab, setTab] = useState("inventory");
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [production, setProduction] = useState({}); // { [id]: number | {full,partial,loose,showLoose,touched} }
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locFilter, setLocFilter] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  // Admin inline-edit state for Catalog-style edits directly on the inventory table
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({});
  // Franchisee-style set-count staging values keyed by item id (admin only, on inventory tab)
  const [setVals, setSetVals] = useState({});
  const sectionRefs = useRef({});

  const refresh = () => {
    const url = isAdmin && locFilter ? `/inventory?location_id=${encodeURIComponent(locFilter)}` : "/inventory";
    return api.get(url).then((r) => {
      setItems(r.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  };

  useEffect(() => {
    if (isAdmin) {
      api.get("/locations").then((r) => {
        setLocations(r.data);
        setLocFilter((prev) => prev || r.data.find((l) => l.name === "Kitchen")?.id || r.data[0]?.id || "");
      });
    } else {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin && locFilter) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locFilter]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return items.filter((i) => !q || i.name.toLowerCase().includes(q));
  }, [items, query]);

  const grouped = useMemo(() => {
    const buckets = {};
    for (const it of filtered) (buckets[it.category] ||= []).push(it);
    const keys = [
      ...CATEGORY_ORDER.filter((k) => buckets[k]),
      ...Object.keys(buckets).filter((k) => !CATEGORY_ORDER.includes(k)),
    ];
    return keys.map((k) => ({
      key: k, label: CATEGORY_LABELS[k] || k,
      rows: buckets[k].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [filtered]);

  // ─── Today's Count (ADDITIVE: adds produced-today qty onto current stock) ───
  const setTotal = (id, v) => {
    const n = v === "" ? "" : Math.max(0, parseInt(v, 10) || 0);
    setProduction((p) => ({ ...p, [id]: n }));
  };
  // Kitchen produces in full blisters/pans only — no Partial column here.
  // Kept the setter signature so any legacy caller still works, but the UI
  // no longer renders a Partial input at all on the Kitchen production tab.
  const setBlister = (id, key, v) => {
    const n = v === "" ? "" : Math.max(0, parseInt(v, 10) || 0);
    setProduction((p) => {
      const prev = typeof p[id] === "object" && p[id] !== null ? p[id] : { full: "", partial: "" };
      return { ...p, [id]: { ...prev, [key]: n, touched: true } };
    });
  };

  const recountLines = useMemo(
    () => Object.entries(production).filter(([, v]) => totalOf(v) !== null).length,
    [production],
  );

  const submitRecount = async () => {
    const entries = [];
    for (const [item_id, v] of Object.entries(production)) {
      const total = totalOf(v);
      if (total === null || total <= 0) continue;
      entries.push({ item_id, quantity: total });
    }
    if (entries.length === 0) {
      toast.error("Enter a produced quantity for at least one item");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/production", { entries, notes: notes || null });
      toast.success(`Produced today saved — ${data.updated.length} item(s) added to on hand`);
      setProduction({});
      setNotes("");
      refresh();
      setTab("inventory");
    } catch (e) {
      toast.error("Couldn't save today's production", { description: formatApiError(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  // ─── Admin: Catalog-parity actions on the inventory tab ───
  // For macaron rows, the input is Full × 70 + Partial (0–70). Stored as
  // { full, partial } and reduced to a total on save. Non-macaron rows keep
  // a single numeric input for a direct absolute count.
  const setCount = async (item) => {
    const raw = setVals[item.id];
    if (raw === "" || raw === undefined || raw === null) return;
    let v;
    if (BLISTER_CATEGORIES.has(item.category) && typeof raw === "object") {
      const f = Math.max(0, parseInt(raw.full, 10) || 0);
      const p = Math.max(0, Math.min(70, parseInt(raw.partial, 10) || 0));
      v = f * FULL_TRAY + p;
    } else {
      v = Math.max(0, parseInt(raw, 10) || 0);
    }
    if (typeof raw === "object" && (raw.full ?? "") === "" && (raw.partial ?? "") === "") return;
    try {
      const { data } = await api.post(`/inventory/${item.id}/adjust`, { delta: 0, set_value: v, note: "Admin set-count" });
      setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, current_stock: data.current_stock } : i));
      setSetVals((s) => ({ ...s, [item.id]: "" }));
      toast.success(`${item.name} set to ${data.current_stock}`);
    } catch (e) {
      toast.error("Couldn't set count", { description: formatApiError(e.response?.data?.detail) || e.message });
    }
  };
  const startEdit = (i) => { setEditingId(i.id); setEdit({ price: i.price }); };
  const saveEdit = async (id) => {
    try {
      await api.patch(`/inventory/${id}`, {
        price: parseFloat(edit.price),
      });
      toast.success("Item updated");
      setEditingId(null);
      refresh();
    } catch (e) {
      toast.error("Couldn't save", { description: formatApiError(e.response?.data?.detail) || e.message });
    }
  };
  const toggleAvailable = async (i) => {
    try {
      await api.patch(`/inventory/${i.id}`, { available: !i.available });
      toast.success(`${i.name} — ${!i.available ? "shown" : "hidden"} in the order form`);
      refresh();
    } catch (e) {
      toast.error("Couldn't update", { description: formatApiError(e.response?.data?.detail) || e.message });
    }
  };
  const removeItem = async (i) => {
    if (!window.confirm(`Delete ${i.name} from ${locations.find((l) => l.id === locFilter)?.name || "this kitchen"}?`)) return;
    try {
      await api.delete(`/inventory/${i.id}`);
      toast.success(`Removed "${i.name}"`);
      refresh();
    } catch (e) {
      toast.error("Couldn't delete", { description: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  const jumpTo = (key) => {
    const el = sectionRefs.current[key];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const lowCount = items.filter((i) => i.current_stock <= (i.low_stock_threshold || 0)).length;

  return (
    <Layout>
      <PageHeader
        eyebrow={isAdmin
          ? `Inventory · ${locations.find((l) => l.id === locFilter)?.name || "Select a kitchen"}`
          : user?.warehouse ? `Warehouse · ${user.location_name || "My location"}` : "Kitchen"}
        title={isAdmin ? "Inventory" : user?.warehouse ? "Warehouse Inventory" : "Kitchen Quick Log"}
        description={isAdmin
          ? "Live inventory for the selected kitchen. Type the actual count and Save; edit price/threshold, toggle Available, or remove items right from this table."
          : user?.warehouse ? `Live ${user.location_name || ""} warehouse stock. Type the actual count and Save; use Daily Production to add produced-today units.` : "Live Kitchen inventory. Type the actual count and Save; toggle Available, delete items, or add a new flavor. Use Daily Production to add produced-today units."}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && locations.length > 0 && (
              <select
                value={locFilter}
                onChange={(e) => setLocFilter(e.target.value)}
                data-testid="admin-inventory-location-picker"
                className="h-11 px-3 rounded-xl border border-border bg-card text-sm font-medium"
              >
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            )}
            {isAdmin && (
              <button
                onClick={() => nav("/admin/opening-inventory")}
                data-testid="opening-inventory-btn"
                className="h-11 px-4 rounded-xl border border-border bg-card text-sm font-medium hover:bg-secondary inline-flex items-center gap-2"
              >
                <BarChart3 className="h-4 w-4" /> Opening Inventory
              </button>
            )}
            {canEditInventory && (
              <button
                onClick={() => setAddOpen(true)}
                data-testid={isAdmin ? "admin-add-item-btn" : "kitchen-add-flavor-btn"}
                className="h-11 px-4 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 inline-flex items-center gap-2"
              >
                <Plus className="h-4 w-4" /> {isAdmin ? "Add Item" : "Add Flavor"}
              </button>
            )}
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap" data-testid="kitchen-tabs">
        <button
          onClick={() => setTab("inventory")}
          data-testid="tab-current-inventory"
          className={`h-11 px-5 rounded-full text-sm font-medium inline-flex items-center gap-2 ${
            tab === "inventory" ? "bg-foreground text-background" : "bg-card border border-border text-muted-foreground hover:text-foreground"
          }`}
        >
          <Boxes className="h-4 w-4" /> Current Inventory
        </button>
        <button
          onClick={() => setTab("production")}
          data-testid="tab-daily-production"
          className={`h-11 px-5 rounded-full text-sm font-medium inline-flex items-center gap-2 ${
            tab === "production" ? "bg-foreground text-background" : "bg-card border border-border text-muted-foreground hover:text-foreground"
          }`}
        >
          <ClipboardCheck className="h-4 w-4" /> Daily Production Entry
        </button>
      </div>

      {/* Search + quick-jump pills */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search all categories…"
            data-testid="kitchen-search"
            className="h-11 pl-9 pr-3 rounded-xl border border-input bg-card text-sm w-72"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {loaded ? `${filtered.length} items · ${lowCount} low` : "Loading…"}
        </span>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="kitchen-category-pills">
        {grouped.map((g) => (
          <button
            key={g.key}
            onClick={() => jumpTo(g.key)}
            data-testid={`kitchen-jump-${g.key}`}
            className="h-9 px-4 rounded-full text-xs font-medium bg-card border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
          >
            {g.label} <span className="text-[10px] opacity-60">· {g.rows.length}</span>
          </button>
        ))}
      </div>

      {/* Current Inventory tab */}
      {tab === "inventory" && (
        <div className="space-y-8" data-testid="current-inventory-panel">
          {loaded && grouped.length === 0 && (
            <div className="luxury-card p-12 text-center text-sm text-muted-foreground">
              No items match your search.
            </div>
          )}
          {grouped.map((g) => {
            const isBlisterSection = BLISTER_CATEGORIES.has(g.key);
            return (
            <section
              key={g.key}
              ref={(el) => { sectionRefs.current[g.key] = el; }}
              data-testid={`kitchen-section-${g.key}`}
            >
              <h2 className="font-display text-xl font-medium tracking-tight mb-3 flex items-baseline gap-3">
                {g.label}
                <span className="text-xs font-normal text-muted-foreground uppercase tracking-wider">
                  {g.rows.length} item{g.rows.length !== 1 ? "s" : ""}
                </span>
              </h2>
              <div className="luxury-card overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/60">
                    <tr className="text-left">
                      <th className="px-5 py-3 label-eyebrow font-medium">Item</th>
                      <th className="px-5 py-3 label-eyebrow font-medium">Pack</th>
                      <th className="px-5 py-3 label-eyebrow font-medium text-right">On Hand</th>
                      {canEditInventory && (
                        <>
                          {isBlisterSection && (
                            <>
                              <th className="px-5 py-3 label-eyebrow font-medium text-right w-24">Full × 70</th>
                              <th className="px-5 py-3 label-eyebrow font-medium text-right w-24">Partial (0–70)</th>
                            </>
                          )}
                          <th className="px-5 py-3 label-eyebrow font-medium text-right w-28">
                            {isBlisterSection ? "= Set Count" : "Set Count"}
                          </th>
                          {canSeePrice && <th className="px-5 py-3 label-eyebrow font-medium text-right w-24">Price</th>}
                          <th className="px-5 py-3 label-eyebrow font-medium text-right w-56">Actions</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((i) => {
                      const low = i.current_stock <= (i.low_stock_threshold || 0);
                      const editing = editingId === i.id;
                      return (
                        <tr key={i.id} className="border-t border-border">
                          <td className="px-5 py-3 font-medium">
                            {i.name}
                            {i.flavor && i.flavor !== i.name && (
                              <span className="text-muted-foreground text-xs ml-2">· {i.flavor}</span>
                            )}
                            {canEditInventory && !i.available && (
                              <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary border border-border px-2 py-0.5 rounded-full">Hidden</span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-muted-foreground">
                            {isIndividual(i) ? <span className="text-xs">{unitText(i)}</span> : i.pack_size ? (
                              <span className="inline-flex items-center gap-1.5">
                                <span className="font-medium text-foreground tabular-nums">{i.pack_size}</span>
                                <span className="text-xs">/ {i.unit}</span>
                              </span>
                            ) : i.unit}
                          </td>
                          <td className={`px-5 py-3 text-right font-medium tabular-nums ${low ? "text-destructive" : ""}`}
                              data-testid={`stock-${i.id}`}>
                            {BLISTER_CATEGORIES.has(i.category) ? blisterLabel(i.current_stock) : i.current_stock}
                          </td>
                          {canEditInventory && (
                            <>
                              {isBlisterSection && (
                                <>
                                  <td className="px-5 py-3 text-right">
                                    <input
                                      type="number" min="0" inputMode="numeric"
                                      value={(typeof setVals[i.id] === "object" ? setVals[i.id].full : "") ?? ""}
                                      onChange={(e) => {
                                        const raw = e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0);
                                        setSetVals((s) => ({ ...s, [i.id]: { ...(typeof s[i.id] === "object" ? s[i.id] : {}), full: raw } }));
                                      }}
                                      onKeyDown={(e) => { if (e.key === "Enter") setCount(i); }}
                                      placeholder="0"
                                      data-testid={`inv-set-full-${i.id}`}
                                      className="h-9 w-20 rounded-lg border border-input bg-card px-2 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    />
                                  </td>
                                  <td className="px-5 py-3 text-right">
                                    <input
                                      type="number" min="0" max="70" inputMode="numeric"
                                      value={(typeof setVals[i.id] === "object" ? setVals[i.id].partial : "") ?? ""}
                                      onChange={(e) => {
                                        const raw = e.target.value === "" ? "" : Math.max(0, Math.min(70, parseInt(e.target.value, 10) || 0));
                                        setSetVals((s) => ({ ...s, [i.id]: { ...(typeof s[i.id] === "object" ? s[i.id] : {}), partial: raw } }));
                                      }}
                                      onKeyDown={(e) => { if (e.key === "Enter") setCount(i); }}
                                      placeholder="0"
                                      data-testid={`inv-set-partial-${i.id}`}
                                      className="h-9 w-20 rounded-lg border border-input bg-card px-2 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    />
                                  </td>
                                </>
                              )}
                              <td className="px-5 py-3 text-right">
                                <div className="inline-flex items-center gap-1.5">
                                  {isBlisterSection ? (
                                    <span className="font-display text-base tabular-nums" data-testid={`inv-set-preview-${i.id}`}>
                                      {(Number(setVals[i.id]?.full) || 0) * FULL_TRAY + (Number(setVals[i.id]?.partial) || 0)}
                                    </span>
                                  ) : (
                                    <input
                                      type="number" min="0" inputMode="numeric"
                                      value={typeof setVals[i.id] === "object" ? "" : (setVals[i.id] ?? "")}
                                      onChange={(e) => setSetVals((s) => ({ ...s, [i.id]: e.target.value }))}
                                      onKeyDown={(e) => { if (e.key === "Enter") setCount(i); }}
                                      placeholder="—"
                                      data-testid={`inv-set-${i.id}`}
                                      className="h-9 w-20 rounded-lg border border-input bg-card px-2 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    />
                                  )}
                                  <button
                                    onClick={() => setCount(i)}
                                    disabled={
                                      isBlisterSection
                                        ? (setVals[i.id]?.full ?? "") === "" && (setVals[i.id]?.partial ?? "") === ""
                                        : setVals[i.id] === "" || setVals[i.id] === undefined
                                    }
                                    data-testid={`inv-set-btn-${i.id}`}
                                    className="h-9 w-9 rounded-lg bg-foreground text-background flex items-center justify-center disabled:opacity-30"
                                    aria-label={`Save count for ${i.name}`}
                                  >
                                    <Save className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </td>
                              {canSeePrice && (
                                <td className="px-5 py-3 text-right">
                                  {editing ? (
                                    <input
                                      type="number" min="0" step="0.01" value={edit.price}
                                      onChange={(e) => setEdit({ ...edit, price: e.target.value })}
                                      data-testid={`inv-edit-price-${i.id}`}
                                      className="h-8 w-24 text-right text-sm rounded-md border border-input bg-card px-2"
                                    />
                                  ) : (
                                    <span className="font-medium tabular-nums">${(i.price ?? 0).toFixed(2)}</span>
                                  )}
                                </td>
                              )}
                              <td className="px-5 py-3 text-right">
                                {editing ? (
                                  <div className="inline-flex items-center gap-1">
                                    <button
                                      onClick={() => saveEdit(i.id)}
                                      data-testid={`inv-save-edit-${i.id}`}
                                      className="h-8 w-8 rounded-md bg-foreground text-background flex items-center justify-center"
                                    >
                                      <Check className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      onClick={() => setEditingId(null)}
                                      className="h-8 w-8 rounded-md border border-border flex items-center justify-center"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="inline-flex items-center gap-3">
                                    <button
                                      onClick={() => toggleAvailable(i)}
                                      data-testid={`inv-toggle-available-${i.id}`}
                                      title={i.available ? "Hide from order form" : "Show in order form"}
                                      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-medium border ${
                                        i.available
                                          ? "border-[hsl(var(--accent-sage))]/40 text-[hsl(var(--accent-sage))] hover:bg-[hsl(var(--accent-sage-soft))]"
                                          : "border-border text-muted-foreground hover:bg-secondary"
                                      }`}
                                    >
                                      {i.available ? "Available" : "Hidden"}
                                    </button>
                                    {canSeePrice && (
                                      <button
                                        onClick={() => startEdit(i)}
                                        data-testid={`inv-edit-item-${i.id}`}
                                        className="text-sm text-muted-foreground hover:text-foreground"
                                      >
                                        Edit
                                      </button>
                                    )}
                                    <button
                                      onClick={() => removeItem(i)}
                                      data-testid={`inv-delete-item-${i.id}`}
                                      className="text-muted-foreground hover:text-destructive p-1"
                                      aria-label={`Delete ${i.name}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {KITCHEN_ADDABLE.includes(g.key) && (
                <InventoryQuickAdd
                  category={g.key}
                  categoryLabel={g.label}
                  locationId={isAdmin ? locFilter : undefined}
                  onSaved={refresh}
                />
              )}
            </section>
            );
          })}
        </div>
      )}

      {/* Today's Count (ADDITIVE production tally) tab */}
      {tab === "production" && (
        <div data-testid="daily-production-panel">
          <div className="luxury-card p-5 mb-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="label-eyebrow">Daily Production</div>
              <div className="font-display text-2xl font-light tracking-tight mt-1">
                Produced today · adds to on hand
              </div>
              <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                Enter how many you produced today. Numbers <b>add</b> to the current on-hand count — this is a delta, not a recount. Macarons: <b>Full × 70 + Partial</b> (0–70). Everything else: single pan/pint count.
              </p>
            </div>
            <button
              onClick={submitRecount}
              disabled={busy || recountLines === 0}
              data-testid="submit-production-btn"
              className="h-12 px-6 rounded-xl bg-foreground text-background font-medium text-sm hover:bg-foreground/90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <ClipboardCheck className="h-4 w-4" />
              {busy ? "Saving…" : `Save Production${recountLines > 0 ? ` · ${recountLines} item${recountLines > 1 ? "s" : ""}` : ""}`}
            </button>
          </div>

          <div className="space-y-8">
            {grouped.map((g) => {
              const isBlisterSection = BLISTER_CATEGORIES.has(g.key);
              return (
              <section
                key={g.key}
                ref={(el) => { sectionRefs.current[g.key] = el; }}
                data-testid={`production-section-${g.key}`}
              >
                <h2 className="font-display text-xl font-medium tracking-tight mb-3 flex items-baseline gap-3">
                  {g.label}
                  <span className="text-xs font-normal text-muted-foreground uppercase tracking-wider">
                    {g.rows.length} item{g.rows.length !== 1 ? "s" : ""}
                  </span>
                </h2>
                <div className="luxury-card overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/60">
                      <tr className="text-left">
                        <th className="px-5 py-3 label-eyebrow font-medium">Item</th>
                        <th className="px-5 py-3 label-eyebrow font-medium">Pack</th>
                        <th className="px-5 py-3 label-eyebrow font-medium text-right w-28">On Hand</th>
                        {isBlisterSection ? (
                          <>
                            <th className="px-5 py-3 label-eyebrow font-medium text-right w-24">Full × 70</th>
                            <th className="px-5 py-3 label-eyebrow font-medium text-right w-28">Partial (0–70)</th>
                            <th className="px-5 py-3 label-eyebrow font-medium text-right w-28">= Added</th>
                          </>
                        ) : (
                          <th className="px-5 py-3 label-eyebrow font-medium text-right w-40">Produced today</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map((i) => (
                        <ProductionRow
                          key={i.id}
                          item={i}
                          isBlister={isBlisterSection}
                          entry={production[i.id]}
                          onTotal={(v) => setTotal(i.id, v)}
                          onBlister={(k, v) => setBlister(i.id, k, v)}
                          blisterTotal={totalOf(production[i.id]) || 0}
                        />
                      ))}
                      {!isAdmin && KITCHEN_ADDABLE.includes(g.key) && (
                        <CustomFlavorRow
                          category={g.key}
                          categoryLabel={g.label}
                          onSaved={refresh}
                        />
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
              );
            })}
          </div>

          <div className="mt-5">
            <label className="label-eyebrow mb-2 block">Shift notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Anything the manager should know?"
              data-testid="production-notes"
              className="w-full rounded-xl border border-input bg-card px-3 py-2 text-sm"
            />
          </div>
        </div>
      )}

      {addOpen && !isAdmin && (
        <AddCustomFlavorModal
          onClose={() => setAddOpen(false)}
          onCreated={() => { setAddOpen(false); refresh(); }}
        />
      )}
      {addOpen && isAdmin && (
        <AdminAddItemModal
          locationId={locFilter}
          locationName={locations.find((l) => l.id === locFilter)?.name || ""}
          onClose={() => setAddOpen(false)}
          onCreated={() => { setAddOpen(false); refresh(); }}
        />
      )}
    </Layout>
  );
}


/**
 * Compact "+ Add flavor" strip that sits below every macaron/gelato section
 * on the Current Inventory tab. One row → name + qty + save. Works for
 * admin (writes to the picked kitchen), kitchen employees, and franchisees
 * (auto-scoped to their own location server-side).
 */
function InventoryQuickAdd({ category, categoryLabel, locationId, onSaved }) {
  const [name, setName] = useState("");
  const [entry, setEntry] = useState({});
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [premium, setPremium] = useState(false);
  const isBlister = BLISTER_CATEGORIES.has(category);
  const total = isBlister
    ? (Number(entry.full) || 0) * FULL_TRAY + Math.min(70, Number(entry.partial) || 0)
    : (Number(qty) || 0);

  const save = async () => {
    if (!name.trim()) { toast.error("Type a flavor name first"); return; }
    setBusy(true);
    try {
      const packSize = category === "macaron" || category === "macaron_seasonal" ? 70 : 1;
      const unit = category === "macaron" || category === "macaron_seasonal" ? "box (70 pcs)" : "pan";
      const body = {
        name: name.trim(), flavor: name.trim(), category,
        unit, pack_size: packSize, deduction_qty: 1,
        low_stock_threshold: 5, current_stock: 0, price: 0, premium, available: true,
      };
      if (locationId) body.location_id = locationId;
      const { data: created } = await api.post("/inventory", body);
      await api.post("/inventory/recount", {
        entries: [{ item_id: created.id, quantity: total }],
      });
      toast.success(`${name.trim()} added — ${total} on hand`);
      setName("");
      setEntry({});
      setQty("");
      onSaved?.();
    } catch (e) {
      toast.error("Couldn't add flavor", { description: formatApiError(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-border bg-secondary/30 px-3 py-2"
      data-testid={`inv-quick-add-${category}`}
    >
      <Plus className="h-3.5 w-3.5 text-muted-foreground" />
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={`Add ${categoryLabel} flavor…`}
        data-testid={`inv-quick-add-name-${category}`}
        className="h-9 flex-1 min-w-[10rem] max-w-xs rounded-lg border border-input bg-card px-3 text-sm placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
      {isBlister ? (
        <>
          <input
            type="number" min="0" inputMode="numeric"
            value={entry.full ?? ""}
            onChange={(e) => setEntry((p) => ({ ...p, full: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0) }))}
            placeholder="Full"
            title="Full boxes (× 70)"
            data-testid={`inv-quick-add-full-${category}`}
            className="h-9 w-16 rounded-lg border border-input bg-card px-2 text-right text-sm tabular-nums"
          />
          <span className="text-[10px] text-muted-foreground">×70+</span>
          <input
            type="number" min="0" max="70" inputMode="numeric"
            value={entry.partial ?? ""}
            onChange={(e) => {
              const raw = e.target.value === "" ? "" : Math.max(0, Math.min(70, parseInt(e.target.value, 10) || 0));
              setEntry((p) => ({ ...p, partial: raw }));
            }}
            placeholder="Partial"
            title="Partial (0–70)"
            data-testid={`inv-quick-add-partial-${category}`}
            className="h-9 w-20 rounded-lg border border-input bg-card px-2 text-right text-sm tabular-nums"
          />
          <span className="text-[10px] text-muted-foreground tabular-nums">= {total}</span>
        </>
      ) : (
        <input
          type="number" min="0" inputMode="numeric"
          value={qty}
          onChange={(e) => setQty(e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0))}
          placeholder="Count"
          data-testid={`inv-quick-add-qty-${category}`}
          className="h-9 w-24 rounded-lg border border-input bg-card px-2 text-right text-sm tabular-nums"
        />
      )}
      <PremiumToggle value={premium} onChange={setPremium} allowWarehouse />
      <button
        onClick={save}
        disabled={busy || !name.trim()}
        data-testid={`inv-quick-add-save-${category}`}
        className="h-9 px-3 rounded-lg bg-foreground text-background text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-40"
      >
        <Save className="h-3.5 w-3.5" /> Add
      </button>
    </div>
  );
}


/**
 * Compact Full×70 + Partial(0–70) widget for the admin Current Inventory
 * Actual Count column. Same math as RecountRow but sized to fit inline.
 */
function AdminBlisterActual({ itemId, value, onChange, onSave }) {
  const full = value.full ?? "";
  const partial = value.partial ?? "";
  const total = (Number(full) || 0) * FULL_TRAY + (Number(partial) || 0);
  const setFull = (v) => onChange({ ...value, full: v === "" ? "" : Math.max(0, parseInt(v, 10) || 0) });
  const setPartial = (v) => onChange({ ...value, partial: v === "" ? "" : Math.max(0, Math.min(70, parseInt(v, 10) || 0)) });
  return (
    <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
      <input
        type="number" min="0" inputMode="numeric" value={full}
        onChange={(e) => setFull(e.target.value)}
        placeholder="F"
        title="Full boxes (× 70)"
        data-testid={`inv-set-full-${itemId}`}
        className="h-9 w-14 rounded-lg border border-input bg-card px-2 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
      <span className="text-xs text-muted-foreground">×70+</span>
      <input
        type="number" min="0" max="70" inputMode="numeric" value={partial}
        onChange={(e) => setPartial(e.target.value)}
        placeholder="P"
        title="Partial (0–70)"
        data-testid={`inv-set-partial-${itemId}`}
        className="h-9 w-14 rounded-lg border border-input bg-card px-2 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
      <span className="text-xs text-muted-foreground tabular-nums" data-testid={`inv-set-preview-${itemId}`}>
        = {total}
      </span>
      <button
        onClick={onSave}
        disabled={full === "" && partial === ""}
        data-testid={`inv-set-btn-${itemId}`}
        className="h-9 w-9 rounded-lg bg-foreground text-background flex items-center justify-center disabled:opacity-30"
        aria-label="Save actual count"
      >
        <Save className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}


/**
 * Inline blank row at the bottom of each addable production section.
 * Kitchen employee types a flavor name + qty and taps Save. Behind the
 * scenes we POST /inventory to create the item at their location, then
 * POST /inventory/recount to set today's count. No modal, no extra step.
 */
function CustomFlavorRow({ category, categoryLabel, onSaved }) {
  const [name, setName] = useState("");
  const [entry, setEntry] = useState({});   // { full, partial } for blister; number for gelato
  const [busy, setBusy] = useState(false);
  const [premium, setPremium] = useState(false);
  const isBlister = BLISTER_CATEGORIES.has(category);
  const total = isBlister
    ? (Number(entry.full) || 0) * FULL_TRAY + Math.min(70, Number(entry.partial) || 0)
    : (Number(entry) || 0);

  const save = async () => {
    if (!name.trim()) { toast.error("Type a flavor name first"); return; }
    setBusy(true);
    try {
      const packSize = category === "macaron" || category === "macaron_seasonal" ? 70 : 1;
      const unit = category === "macaron" || category === "macaron_seasonal" ? "box (70 pcs)" : "pan";
      const { data: created } = await api.post("/inventory", {
        name: name.trim(), flavor: name.trim(), category,
        unit, pack_size: packSize, deduction_qty: 1,
        low_stock_threshold: 5, current_stock: 0, price: 0, premium, available: true,
      });
      await api.post("/inventory/recount", {
        entries: [{ item_id: created.id, quantity: total }],
      });
      toast.success(`${name.trim()} added — ${total} on hand`);
      setName("");
      setEntry(isBlister ? {} : "");
      onSaved?.();
    } catch (e) {
      toast.error("Couldn't add flavor", { description: formatApiError(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-t border-border bg-secondary/20">
      <td className="px-5 py-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={`+ Add ${categoryLabel} flavor…`}
          data-testid={`custom-flavor-name-${category}`}
          className="h-11 w-full max-w-xs rounded-lg border border-dashed border-input bg-card px-3 text-sm placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </td>
      <td className="px-5 py-3 text-muted-foreground text-xs">
        {isBlister ? "70 / box · Blister" : "count"}
      </td>
      <td className="px-5 py-3 text-right text-muted-foreground tabular-nums">—</td>
      {isBlister ? (
        <>
          <td className="px-5 py-3 text-right">
            <input
              type="number" min="0" inputMode="numeric"
              value={entry.full ?? ""}
              onChange={(e) => setEntry((prev) => ({ ...prev, full: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0) }))}
              placeholder="0"
              data-testid={`custom-flavor-${category}-full`}
              className="h-11 w-20 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums"
            />
          </td>
          <td className="px-5 py-3 text-right">
            <input
              type="number" min="0" max="70" inputMode="numeric"
              value={entry.partial ?? ""}
              onChange={(e) => {
                const raw = e.target.value === "" ? "" : Math.max(0, Math.min(70, parseInt(e.target.value, 10) || 0));
                setEntry((prev) => ({ ...prev, partial: raw }));
              }}
              placeholder="0"
              data-testid={`custom-flavor-${category}-partial`}
              className="h-11 w-20 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums"
            />
          </td>
          <td className="px-5 py-3">
            <div className="flex items-center justify-end gap-2">
              <span className="font-display text-lg tabular-nums">{total || 0}</span>
              <PremiumToggle value={premium} onChange={setPremium} allowWarehouse />
              <button
                onClick={save}
                disabled={busy || !name.trim()}
                data-testid={`custom-flavor-save-${category}`}
                className="h-9 w-9 rounded-lg bg-foreground text-background flex items-center justify-center disabled:opacity-40"
                aria-label="Save custom flavor"
              >
                <Save className="h-3.5 w-3.5" />
              </button>
            </div>
          </td>
        </>
      ) : (
        <td className="px-5 py-3">
          <div className="flex items-center justify-end gap-2">
            <input
              type="number" min="0" inputMode="numeric"
              value={entry ?? ""}
              onChange={(e) => setEntry(e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0))}
              placeholder="0"
              data-testid={`custom-flavor-qty-${category}`}
              className="h-11 w-20 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums"
            />
            <PremiumToggle value={premium} onChange={setPremium} allowWarehouse />
            <button
              onClick={save}
              disabled={busy || !name.trim()}
              data-testid={`custom-flavor-save-${category}`}
              className="h-9 w-9 rounded-lg bg-foreground text-background flex items-center justify-center disabled:opacity-40"
              aria-label="Save custom flavor"
            >
              <Save className="h-3.5 w-3.5" />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}


/**
 * Row for the Today's Count table.
 * Macaron / Seasonal macaron rows show a blister-tray widget; everything
 * else shows a single "Total On Hand" input.
 */
/**
 * Kitchen Daily Production row — Full × 70 + Partial (0–70) widget for
 * macaron & seasonal; single integer input for every other category.
 * Value (or Full×70 + Partial sum) is ADDED to current_stock on save.
 */
function ProductionRow({ item, isBlister, entry, onTotal, onBlister, blisterTotal }) {
  const unitHint = item.pack_size ? `${item.unit}` : item.unit;
  const obj = isBlister && typeof entry === "object" && entry !== null ? entry : {};
  return (
    <tr className="border-t border-border align-top" data-testid={`production-row-${item.id}`}>
      <td className="px-5 py-3 font-medium">{item.name}</td>
      <td className="px-5 py-3 text-muted-foreground">
        {unitText(item)}
      </td>
      <td className="px-5 py-3 text-right text-muted-foreground tabular-nums">{item.current_stock}</td>
      {isBlister ? (
        <>
          <td className="px-5 py-3 text-right">
            <input
              type="number" min="0" inputMode="numeric"
              value={obj.full ?? ""}
              onChange={(e) => onBlister("full", e.target.value)}
              placeholder="0"
              data-testid={`production-${item.id}-full`}
              className="h-11 w-20 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </td>
          <td className="px-5 py-3 text-right">
            <input
              type="number" min="0" max="70" inputMode="numeric"
              value={obj.partial ?? ""}
              onChange={(e) => {
                const raw = Math.max(0, Math.min(70, parseInt(e.target.value, 10) || 0));
                onBlister("partial", e.target.value === "" ? "" : String(raw));
              }}
              placeholder="0"
              data-testid={`production-${item.id}-partial`}
              className="h-11 w-20 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </td>
          <td className="px-5 py-3 text-right">
            <span className="font-display text-lg font-medium tabular-nums" data-testid={`production-${item.id}-total`}>
              {blisterTotal || 0}
            </span>
          </td>
        </>
      ) : (
        <td className="px-5 py-3 text-right">
          <div className="inline-flex items-center gap-2">
            <input
              type="number" min="0" inputMode="numeric"
              value={typeof entry === "number" ? entry : (typeof entry === "string" ? entry : "")}
              onChange={(e) => onTotal(e.target.value)}
              placeholder="0"
              data-testid={`production-${item.id}`}
              className="h-11 w-24 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <span className="text-[10px] text-muted-foreground">{unitHint}</span>
          </div>
        </td>
      )}
    </tr>
  );
}


function RecountRow({ item, entry, onTotal, onBlister }) {
  const isBlister = BLISTER_CATEGORIES.has(item.category);
  const total = totalOf(entry);
  const obj = isBlister && typeof entry === "object" && entry !== null ? entry : {};
  return (
    <tr className="border-t border-border align-top">
      <td className="px-5 py-3 font-medium">{item.name}</td>
      <td className="px-5 py-3 text-muted-foreground">
        {unitText(item)}
      </td>
      <td className="px-5 py-3 text-right text-muted-foreground tabular-nums">{item.current_stock}</td>
      {isBlister ? (
        <>
          <td className="px-5 py-3 text-right">
            <input
              type="number" min="0" inputMode="numeric"
              value={obj.full ?? ""}
              onChange={(e) => onBlister("full", e.target.value)}
              placeholder="0"
              data-testid={`recount-${item.id}-full`}
              className="h-11 w-20 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </td>
          <td className="px-5 py-3 text-right">
            <input
              type="number" min="0" max="70" inputMode="numeric"
              value={obj.partial ?? ""}
              onChange={(e) => {
                const raw = Math.max(0, Math.min(70, parseInt(e.target.value, 10) || 0));
                onBlister("partial", e.target.value === "" ? "" : String(raw));
              }}
              placeholder="0"
              data-testid={`recount-${item.id}-partial`}
              className="h-11 w-20 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </td>
          <td className="px-5 py-3 text-right">
            <span
              className="font-display text-lg font-medium tabular-nums"
              data-testid={`recount-${item.id}-total`}
            >
              {total ?? 0}
            </span>
          </td>
        </>
      ) : (
        <>
          <td className="px-5 py-3 text-right text-muted-foreground text-xs">—</td>
          <td className="px-5 py-3 text-right text-muted-foreground text-xs">—</td>
          <td className="px-5 py-3 text-right">
            <input
              type="number" min="0" inputMode="numeric"
              value={typeof entry === "number" ? entry : (typeof entry === "string" ? entry : "")}
              onChange={(e) => onTotal(e.target.value)}
              placeholder={String(item.current_stock ?? 0)}
              data-testid={`production-${item.id}`}
              className="h-11 w-24 rounded-lg border border-input bg-card px-3 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </td>
        </>
      )}
    </tr>
  );
}

function BlisterInput({ entry, total, onBlister, testIdPrefix }) {
  const obj = typeof entry === "object" && entry !== null ? entry : {};
  return (
    <div className="flex items-center justify-end gap-2 flex-wrap">
      <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <input
          type="number" min="0" inputMode="numeric"
          value={obj.full ?? ""}
          onChange={(e) => onBlister("full", e.target.value)}
          placeholder="0"
          data-testid={`${testIdPrefix}-full`}
          className="h-11 w-16 rounded-lg border border-input bg-card px-2 text-center text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <span>full × 70</span>
      </label>
      <span className="text-muted-foreground text-xs">+</span>
      <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <input
          type="number" min="0" max="70" inputMode="numeric"
          value={obj.partial ?? ""}
          onChange={(e) => {
            const raw = Math.max(0, Math.min(70, parseInt(e.target.value, 10) || 0));
            onBlister("partial", String(raw));
          }}
          placeholder="0"
          data-testid={`${testIdPrefix}-partial`}
          className="h-11 w-16 rounded-lg border border-input bg-card px-2 text-center text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <span>partial (0–70)</span>
      </label>
      <span className="text-xs text-muted-foreground">=</span>
      <span
        className="font-display text-lg font-medium tabular-nums min-w-[3ch] text-right"
        data-testid={`${testIdPrefix}-total`}
      >
        {total ?? 0}
      </span>
    </div>
  );
}


/**
 * Admin: add-item modal used on the Inventory tab. Simpler shape than the
 * Catalog one — always writes to the currently-selected kitchen so the
 * new item lands where the admin is looking.
 */
function AdminAddItemModal({ locationId, locationName, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", category: "macaron", unit: "box (70 pcs)",
    pack_size: 70, price: 0, low_stock_threshold: 5, current_stock: 0,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) { setErr("Enter an item name"); return; }
    if (!locationId) { setErr("Pick a kitchen first"); return; }
    setBusy(true);
    try {
      await api.post("/inventory", {
        name: form.name.trim(),
        flavor: form.name.trim(),
        category: form.category,
        location_id: locationId,
        unit: form.unit,
        pack_size: Math.max(1, parseInt(form.pack_size, 10) || 1),
        deduction_qty: 1,
        price: parseFloat(form.price) || 0,
        low_stock_threshold: Math.max(0, parseInt(form.low_stock_threshold, 10) || 0),
        current_stock: Math.max(0, parseInt(form.current_stock, 10) || 0),
      });
      toast.success(`Added "${form.name}" to ${locationName || "kitchen"}`);
      onCreated();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div
      className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="admin-add-item-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-card rounded-2xl border border-border w-full max-w-md p-8 space-y-4"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-2xl font-light tracking-tight">Add Item</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Adds a new item to <b>{locationName || "this kitchen"}</b>. Use Catalog to add across every kitchen at once.
            </p>
          </div>
          <button
            type="button" onClick={onClose}
            className="h-9 w-9 rounded-full border border-border bg-card flex items-center justify-center text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div>
          <label className="label-eyebrow mb-2 block">Name</label>
          <input
            autoFocus required value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Blood Orange, Signature Vanilla"
            data-testid="admin-new-item-name"
            className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label-eyebrow mb-2 block">Category</label>
            <select
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              data-testid="admin-new-item-category"
              className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm"
            >
              {CATEGORY_ORDER.map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-2 block">Unit</label>
            <input
              value={form.unit}
              onChange={(e) => set("unit", e.target.value)}
              className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          <div>
            <label className="label-eyebrow mb-2 block">Pack</label>
            <input
              type="number" min="1" value={form.pack_size}
              onChange={(e) => set("pack_size", e.target.value)}
              className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm tabular-nums"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-2 block">Price</label>
            <input
              type="number" min="0" step="0.01" value={form.price}
              onChange={(e) => set("price", e.target.value)}
              data-testid="admin-new-item-price"
              className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm tabular-nums"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-2 block">Min</label>
            <input
              type="number" min="0" value={form.low_stock_threshold}
              onChange={(e) => set("low_stock_threshold", e.target.value)}
              className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm tabular-nums"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-2 block">Stock</label>
            <input
              type="number" min="0" value={form.current_stock}
              onChange={(e) => set("current_stock", e.target.value)}
              className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm tabular-nums"
            />
          </div>
        </div>

        {err && <div className="text-xs text-destructive">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button" onClick={onClose}
            className="h-11 px-5 rounded-xl border border-border bg-card text-sm font-medium hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            type="submit" disabled={busy}
            data-testid="admin-add-item-submit"
            className="h-11 px-5 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 disabled:opacity-50"
          >
            {busy ? "Adding…" : "Add Item"}
          </button>
        </div>
      </form>
    </div>
  );
}


function AddCustomFlavorModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("macaron");
  const [initialQty, setInitialQty] = useState(0);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim()) { setErr("Enter a flavor name"); return; }
    setBusy(true);
    try {
      await api.post("/inventory", {
        name: name.trim(),
        flavor: name.trim(),
        category,
        // Sensible defaults so admin doesn't have to fix every field later
        unit: category.startsWith("gelato_pint") ? "case of 6"
             : category.startsWith("gelato_pop")  ? "case of 10"
             : category.startsWith("gelato_pan")  ? "pan"
             : "box (70 pcs)",
        pack_size: category.startsWith("gelato_pint") ? 6
                 : category.startsWith("gelato_pop")  ? 10
                 : category.startsWith("gelato_pan")  ? 1
                 : 70,
        deduction_qty: 1,
        low_stock_threshold: 5,
        current_stock: Math.max(0, parseInt(initialQty, 10) || 0),
        price: 0,  // admin sets from Catalog later
      });
      toast.success(`Added "${name}"`);
      onCreated();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="custom-flavor-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-card rounded-2xl border border-border w-full max-w-md p-8 space-y-4"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-2xl font-light tracking-tight">Add Custom Flavor</h2>
            <p className="text-xs text-muted-foreground mt-1">
              For seasonal or special items your kitchen produces. Admin will set the price from Catalog.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-full border border-border bg-card flex items-center justify-center text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div>
          <label className="label-eyebrow mb-2 block">Flavor Name</label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Blood Orange, Mango Sorbet"
            data-testid="custom-flavor-name"
            className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm"
          />
        </div>

        <div>
          <label className="label-eyebrow mb-2 block">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="custom-flavor-category"
            className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm"
          >
            {KITCHEN_ADDABLE.map((c) => (
              <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="label-eyebrow mb-2 block">Initial Production (optional)</label>
          <input
            type="number"
            min="0"
            inputMode="numeric"
            value={initialQty}
            onChange={(e) => setInitialQty(e.target.value)}
            data-testid="custom-flavor-initial"
            className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm tabular-nums"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            You can also leave this at 0 and log production later.
          </p>
        </div>

        {err && <div className="text-xs text-destructive">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-11 px-5 rounded-xl border border-border bg-card text-sm font-medium hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="custom-flavor-submit"
            className="h-11 px-5 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 disabled:opacity-50"
          >
            {busy ? "Adding…" : "Add Flavor"}
          </button>
        </div>
      </form>
    </div>
  );
}
